import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ColumnDef } from '@tanstack/react-table'
import { AlertTriangle, CheckCircle, Clock, FilePlus, Search } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/admin'
import { Button, Card, Spinner } from '@/components/common'
import { DataTable } from '@/components/ui/data-table'
import { useInvoices, useOutgoingStats } from '@/hooks/useInvoices'
import { invoices as invoicesApi } from '@/utils/api'
import InvoiceDetailModal from './buchhaltung/InvoiceDetailModal'
import { useInvoiceDetail } from './buchhaltung/useInvoiceDetail'
import { DEADLINE_TONE, formatCurrency, formatDate } from './buchhaltung/invoiceUtils'
import OutgoingUploadDialog from './ausgangsrechnungen/OutgoingUploadDialog'
import MarkPaidPopover from './ausgangsrechnungen/MarkPaidPopover'
import {
  customerName,
  dueLabel,
  expectedPayment,
  isOpenState,
  outgoingOpenAmount,
  outgoingState,
  skontoLabel,
  skontoValidOn,
  STATE_CONFIG,
  type OutgoingRow,
} from './ausgangsrechnungen/outgoingUtils'

type StatusFilter = '' | 'open' | 'overdue' | 'paid'

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: '', label: 'Alle' },
  { value: 'open', label: 'Offen' },
  { value: 'overdue', label: 'Überfällig' },
  { value: 'paid', label: 'Bezahlt' },
]

/**
 * Ausgangsrechnungen — eigene Seite neben der Buchhaltung (Okt. 2026).
 *
 * Gleiche Daten (invoices, type='outgoing'), gleicher Look und dasselbe
 * Detail-Fenster wie die Buchhaltung; anders ist nur die Richtung: hier wird
 * nicht ueberwiesen, sondern ein Zahlungseingang abgehakt.
 */
const Ausgangsrechnungen = () => {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [uploadOpen, setUploadOpen] = useState(false)
  const detail = useInvoiceDetail()

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const params: Record<string, string> = { type: 'outgoing' }
  if (statusFilter) params.status = statusFilter
  if (debouncedSearch) params.search = debouncedSearch

  const { data: rows = [], isLoading } = useInvoices(params)
  const { data: stats } = useOutgoingStats()

  // Bezahlt aus der Liste — optimistisch, sonst bleibt die alte Zeile wegen
  // placeholderData stehen und es wirkt, als sei nichts passiert.
  const markPaidMutation = useMutation({
    mutationFn: ({ id, paidAt, applySkonto }: { id: number; paidAt: string; applySkonto: boolean }) =>
      invoicesApi.markPaidBatch([id], applySkonto, paidAt).then((r) => r.data),
    onMutate: async ({ id, paidAt, applySkonto }) => {
      await queryClient.cancelQueries({ queryKey: ['invoices'] })
      const snapshot = queryClient.getQueriesData({ queryKey: ['invoices'] })
      queryClient.setQueriesData({ queryKey: ['invoices'] }, (old: unknown) =>
        Array.isArray(old)
          ? old.map((r: any) => (r.id === id
            ? { ...r, status: 'paid', paid_at: `${paidAt} 12:00:00`, paid_amount: expectedPayment(r, applySkonto && skontoValidOn(r, paidAt)) }
            : r))
          : old,
      )
      return { snapshot }
    },
    onError: (err: any, _vars, ctx) => {
      ctx?.snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data))
      toast.error(err?.response?.data?.error || err?.message || 'Als bezahlt markieren fehlgeschlagen')
    },
    onSuccess: (res) => {
      if (res.markedCount === 0) {
        const reason = res.skipped?.[0]?.reason
        toast.error(reason === 'paid_before_invoice_date'
          ? 'Zahlungsdatum liegt vor dem Rechnungsdatum'
          : 'Rechnung war bereits abgeschlossen')
        return
      }
      const m = res.marked[0]
      toast.success(
        `${m.invoice_number || 'Rechnung'} als bezahlt markiert — ${formatCurrency(m.transferred)} eingegangen`
        + (m.skonto > 0 ? ` (${formatCurrency(m.skonto)} Skonto)` : ''),
      )
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['invoices'] }),
  })

  const columns: ColumnDef<OutgoingRow>[] = useMemo(() => [
    {
      id: 'status',
      accessorFn: (row) => outgoingState(row),
      header: 'Status',
      size: 110,
      cell: ({ row }) => {
        const cfg = STATE_CONFIG[outgoingState(row.original)]
        return (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.color}`}>
            {cfg.label}
          </span>
        )
      },
    },
    {
      id: 'invoice_number',
      accessorFn: (row) => row.invoice_number || '',
      header: 'Rechnungsnr.',
      cell: ({ row }) => (
        <span className="font-mono text-sm text-gray-900">{row.original.invoice_number || '-'}</span>
      ),
    },
    {
      id: 'customer',
      accessorFn: (row) => customerName(row),
      header: 'Kunde',
      cell: ({ row }) => <span className="text-sm font-medium text-gray-900">{customerName(row.original)}</span>,
    },
    {
      id: 'invoice_date',
      accessorFn: (row) => row.invoice_date || '',
      header: 'Rechnungsdatum',
      cell: ({ row }) => <span className="text-sm text-gray-600">{formatDate(row.original.invoice_date)}</span>,
    },
    {
      id: 'gross_amount',
      accessorFn: (row) => row.gross_amount ?? 0,
      header: () => <span className="block text-right">Betrag</span>,
      cell: ({ row }) => {
        const inv = row.original
        const partial = isOpenState(outgoingState(inv)) && (inv.paid_amount || 0) > 0
        if (!partial) return <span className="block text-right text-sm font-medium">{formatCurrency(inv.gross_amount)}</span>
        return (
          <div className="text-right text-sm font-medium">
            <span className="text-xs text-gray-400 line-through">{formatCurrency(inv.gross_amount)}</span>
            <br />
            <span className="text-amber-700">{formatCurrency(Math.max(0, outgoingOpenAmount(inv)))}</span>
            <span className="block text-[10px] font-normal text-amber-600">offen nach Teilzahlung</span>
          </div>
        )
      },
    },
    {
      id: 'due_date',
      // Ohne Zahlungsziel ans Ende — eine Rechnung ohne Frist ist nicht "sofort faellig"
      accessorFn: (row) => row.due_date || '9999-12-31',
      header: 'Fällig',
      cell: ({ row }) => {
        const inv = row.original
        const label = dueLabel(inv)
        const tone = label.tone ? DEADLINE_TONE[label.tone] : null
        return (
          <div className="flex items-start gap-1.5 leading-tight">
            {tone && <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${tone.dot}`} />}
            <div className="min-w-0">
              {inv.due_date && <div className="text-sm tabular-nums text-gray-700">{formatDate(inv.due_date)}</div>}
              {label.text && (
                <div className={`text-xs font-medium ${tone?.text || 'text-gray-400'}`}>{label.text}</div>
              )}
            </div>
          </div>
        )
      },
    },
    {
      id: 'skonto',
      accessorFn: (row) => row.skonto_date || '',
      header: 'Skonto',
      cell: ({ row }) => {
        const inv = row.original
        const text = skontoLabel(inv)
        if (!text) return <span className="text-sm text-gray-300">–</span>
        // Abgelaufene Frist bzw. bezahlte Rechnung: nur noch zur Info
        const active = isOpenState(outgoingState(inv)) && skontoValidOn(inv)
        return <span className={`text-xs ${active ? 'font-medium text-emerald-700' : 'text-gray-400'}`}>{text}</span>
      },
    },
    {
      id: 'action',
      header: '',
      enableSorting: false,
      size: 150,
      cell: ({ row }) => {
        const inv = row.original
        if (outgoingState(inv) === 'cancelled') return null
        if (outgoingState(inv) === 'paid') {
          return (
            <div className="text-right text-xs leading-tight text-green-700">
              {inv.paid_at ? `bezahlt am ${formatDate(inv.paid_at)}` : 'bezahlt'}
              {/* paid_amount ist 0 (nicht NULL), wenn per Statusknopf/Ablage "erledigt" gesetzt */}
              {(inv.paid_amount ?? 0) > 0 && inv.paid_amount !== inv.gross_amount && (
                <span className="block text-gray-500">{formatCurrency(inv.paid_amount)} eingegangen</span>
              )}
            </div>
          )
        }
        return (
          <div className="flex justify-end">
            <MarkPaidPopover
              invoice={inv}
              pending={markPaidMutation.isPending && markPaidMutation.variables?.id === inv.id}
              onConfirm={(paidAt, applySkonto) => markPaidMutation.mutate({ id: inv.id, paidAt, applySkonto })}
            />
          </div>
        )
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [markPaidMutation.isPending, markPaidMutation.variables])

  const kpi = (
    filter: StatusFilter,
    value: number | undefined,
    label: string,
    total: number | undefined,
    color: 'amber' | 'red' | 'green',
    Icon: typeof Clock,
    hint?: string,
  ) => {
    const tones = {
      amber: { value: 'text-amber-600', sub: 'text-amber-500', box: 'bg-amber-50', icon: 'text-amber-600' },
      red: { value: 'text-red-600', sub: 'text-red-500', box: 'bg-red-50', icon: 'text-red-600' },
      green: { value: 'text-green-600', sub: 'text-green-500', box: 'bg-green-50', icon: 'text-green-600' },
    }[color]
    const active = statusFilter === filter
    return (
      <button
        type="button"
        onClick={() => setStatusFilter(active ? '' : filter)}
        className={`h-full rounded-xl text-left transition-shadow ${active ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}
      >
        <Card className="h-full">
          <div className="flex items-center justify-between">
            <div>
              <p className={`stat-card-value ${tones.value}`}>{value ?? 0}</p>
              <p className="stat-card-label">{label}</p>
              <p className={`mt-1 text-xs ${tones.sub}`}>{formatCurrency(total ?? 0)}</p>
              {hint && <p className="mt-0.5 text-[11px] text-gray-400">{hint}</p>}
            </div>
            <div className={`rounded-lg p-3 ${tones.box}`}>
              <Icon className={`h-6 w-6 ${tones.icon}`} />
            </div>
          </div>
        </Card>
      </button>
    )
  }

  return (
    <div>
      <PageHeader
        title="Ausgangsrechnungen"
        description="Eigene Rechnungen an Kunden — Fälligkeiten, Skonto und Zahlungseingang"
        actions={
          <Button icon={FilePlus} onClick={() => setUploadOpen(true)}>
            Ausgangsrechnung anlegen
          </Button>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {kpi('open', stats?.open?.count, 'Offen', stats?.open?.total, 'amber', Clock,
          stats?.dueSoon?.count ? `${stats.dueSoon.count} davon in 7 Tagen fällig` : undefined)}
        {kpi('overdue', stats?.overdue?.count, 'Überfällig', stats?.overdue?.total, 'red', AlertTriangle)}
        {kpi('paid', stats?.paid?.count, 'Bezahlt', stats?.paid?.total, 'green', CheckCircle, 'eingegangen (nach Skonto)')}
      </div>

      <div className="mb-6 rounded-lg bg-white shadow-sm">
        <div className="px-4 py-3">
          <div className="flex flex-wrap gap-4">
            <div className="relative min-w-[200px] max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Kunde, Rechnungsnr. oder Schlagwort..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    statusFilter === tab.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <DataTable
          columns={columns as ColumnDef<any>[]}
          data={rows}
          emptyText={
            statusFilter === 'overdue' ? 'Keine überfälligen Ausgangsrechnungen'
              : statusFilter === 'open' ? 'Keine offenen Ausgangsrechnungen'
              : 'Keine Ausgangsrechnungen gefunden'
          }
          pageSize={50}
          // Stabile Zeilen-IDs: mit Index-Keys haengt ein offenes Bezahlt-Popover nach
          // dem Nachladen an der NAECHSTEN Rechnung und bucht deren ID.
          getRowId={(row: any) => String(row.id)}
          onRowClick={(row: any) => detail.open(row.id)}
          getRowClassName={(row: any) => (outgoingState(row) === 'overdue' ? 'bg-red-50/50' : undefined)}
        />
      )}

      <OutgoingUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onCreated={() => {
          setUploadOpen(false)
          queryClient.invalidateQueries({ queryKey: ['invoices'] })
        }}
      />

      {/* Ausgangsrechnungen haben keine Mahnungs-Zuordnung — onAssign bleibt leer */}
      <InvoiceDetailModal detail={detail} onAssign={() => {}} />
    </div>
  )
}

export default Ausgangsrechnungen
